import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { toggleVariants } from '@/components/ui/toggle';
import { cn } from '@/lib/cn';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import type { Editor } from '@tiptap/react';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import type { FormatAction } from '../types';
import { getShortcutKey } from '../utils';
import { ShortcutKey } from './shortcut-key';
import { ToolbarButton } from './toolbar-button';

interface ToolbarSectionProps extends VariantProps<typeof toggleVariants> {
  editor: Editor;
  actions: FormatAction[];
  activeActions?: string[];
  mainActionCount?: number;
  dropdownIcon?: React.ReactNode;
  dropdownTooltip?: string;
  dropdownClassName?: string;
}

export function ToolbarSection({
  editor,
  actions,
  activeActions = actions.map((action) => action.value),
  mainActionCount = 0,
  dropdownIcon,
  dropdownTooltip = 'More options',
  dropdownClassName = 'w-12',
  size,
  variant,
}: ToolbarSectionProps) {
  const { mainActions, dropdownActions } = React.useMemo(() => {
    const sortedActions = actions
      .filter((action) => activeActions.includes(action.value))
      .sort(
        (a, b) =>
          activeActions.indexOf(a.value) - activeActions.indexOf(b.value)
      );

    return {
      mainActions: sortedActions.slice(0, mainActionCount),
      dropdownActions: sortedActions.slice(mainActionCount),
    };
  }, [actions, activeActions, mainActionCount]);

  const isDropdownActive = React.useMemo(
    () => dropdownActions.some((action) => action.isActive(editor)),
    [dropdownActions, editor]
  );

  return (
    <>
      {mainActions.map((action) => {
        const shortcutLabel = action.shortcuts
          ?.map((shortcut) => getShortcutKey(shortcut).symbol)
          .join(' ');
        const tooltip = shortcutLabel
          ? `${action.label} ${shortcutLabel}`
          : action.label;

        return (
          <ToolbarButton
            key={action.value + action.label}
            onClick={() => action.action(editor)}
            disabled={!action.canExecute(editor)}
            isActive={action.isActive(editor)}
            tooltip={tooltip}
            aria-label={action.label}
            size={size}
            variant={variant}
          >
            {action.icon}
          </ToolbarButton>
        );
      })}
      {dropdownActions.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <ToolbarButton
              isActive={isDropdownActive}
              tooltip={dropdownTooltip}
              aria-label={dropdownTooltip}
              className={cn(dropdownClassName)}
              size={size}
              variant={variant}
            >
              {dropdownIcon || <ChevronDownIcon className='size-5' />}
            </ToolbarButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align='start'
            sideOffset={12}
            className='max-h-72 w-full overflow-y-auto'
            onCloseAutoFocus={(event) => event.preventDefault()}
          >
            {dropdownActions.map((action) => (
              <DropdownMenuItem
                key={action.value + action.label}
                onClick={() => action.action(editor)}
                disabled={!action.canExecute(editor)}
                style={action.dropdownMenuItemStyle}
                className={cn(
                  'my-1 flex flex-row items-center justify-between gap-4',
                  {
                    'bg-accent': action.isActive(editor),
                  }
                )}
                aria-label={action.label}
              >
                <span className='grow'>{action.label}</span>
                {action.shortcuts && <ShortcutKey keys={action.shortcuts} />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}
